// filepath: apps/web/src/features/admin/recordPermissions/RecordFieldPermissionsPage.tsx
//
// FR-UI-ADM-2 AC-1 — matrix layout.
//
// Rows: roles (system roles pinned to top via `is_system DESC, name ASC`).
// Columns: entity types.
//
// Each cell contains three action buttons:
//   - "Ownership rule" → opens RecordRuleEditorDialog (AC-2)
//   - "Field permissions" → opens FieldPermissionsDialog (AC-3)
//   - "Owner field" → opens EntityFieldOwnerDialog (AC-1)
//
// Above the matrix: PendingReviewBanner (FR-UI-ADM-1) + the 5
// FR-UI-CORE-10 fetch error states when applicable.
//
// All text renders as JSX text nodes (INV-9).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useEntityTypeList, useRoleList } from "@/features/admin/queries";
import { PendingReviewBanner } from "@/features/admin/PendingReviewBanner";
import { classifyRecordPermissionError } from "./queries";
import { RecordRuleEditorDialog } from "./RecordRuleEditorDialog";
import { FieldPermissionsDialog } from "./FieldPermissionsDialog";
import { EntityFieldOwnerDialog } from "./EntityFieldOwnerDialog";
import { RecordPermissionConflictDialog } from "./RecordPermissionConflictDialog";
import type { FieldPermissionGrant, RecordPermissionConflict } from "./types";

interface OpenDialogs {
  rule: { roleName: string; entityType: string } | null;
  fields: { roleName: string; entityType: string; fields: string[] } | null;
  owner: { entityType: string; fields: string[] } | null;
}

const INITIAL: OpenDialogs = { rule: null, fields: null, owner: null };

export function RecordFieldPermissionsPage(): React.ReactElement {
  const { t } = useTranslation();
  const rolesQuery = useRoleList();
  const entityTypesQuery = useEntityTypeList();
  const [open, setOpen] = useState<OpenDialogs>(INITIAL);
  const [conflict, setConflict] = useState<RecordPermissionConflict | null>(
    null,
  );

  const onConflict = (
    roleName: string,
    entityType: string,
    serverVersion: number,
    serverGrants?: FieldPermissionGrant[],
    serverOwnerField?: string | null,
    serverOwnership?: string | null,
    serverCondition?: string | null,
  ) => {
    setConflict({
      role_name: roleName,
      entity_type: entityType,
      current_version: serverVersion,
      server_grants: serverGrants,
      server_owner_field: serverOwnerField ?? undefined,
      server_ownership_rule: serverOwnership,
      server_condition_rule: serverCondition,
    });
  };

  if (rolesQuery.error || entityTypesQuery.error) {
    const err = classifyRecordPermissionError(
      rolesQuery.error ?? entityTypesQuery.error,
    );
    return (
      <div className="p-4" role="alert" data-testid="record-permissions-error">
        <h2 className="text-sm font-semibold">
          {t("admin.record_permissions.error.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">{err.message}</p>
      </div>
    );
  }

  if (
    rolesQuery.isPending ||
    entityTypesQuery.isPending ||
    !rolesQuery.data ||
    !entityTypesQuery.data
  ) {
    return (
      <div
        className="p-4 text-xs text-muted-foreground"
        data-testid="record-permissions-loading"
      >
        {t("common.loading")}
      </div>
    );
  }

  const roles = rolesQuery.data;
  const entityTypes = entityTypesQuery.data;

  return (
    <div className="p-4" data-testid="record-field-permissions-page">
      <h1 className="text-base font-semibold">
        {t("admin.record_permissions.page_title")}
      </h1>
      <PendingReviewBanner
        pendingCount={null}
        onOpenPromotion={() => {
          // Editor writes auto-feed FR-PROM-1 via audit kinds;
          // promotion review surface is owned by FR-UI-PROM-2.
          // The banner is intentionally read-only in v1 (count
          // resolution deferred to a future workwave).
        }}
      />
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="py-1 pr-2">
                {t("admin.record_permissions.role")}
              </th>
              {entityTypes.map((et) => (
                <th
                  key={et.entity_type}
                  className="py-1 pr-2"
                  data-testid={`entity-type-header-${et.entity_type}`}
                >
                  {et.entity_type}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr
                key={role.name}
                className="border-b border-border"
                data-testid={`role-row-${role.name}`}
              >
                <td className="py-1 pr-2 font-mono">
                  {role.name}
                  {role.is_system ? (
                    <span className="ml-1 text-muted-foreground">(system)</span>
                  ) : null}
                </td>
                {entityTypes.map((et) => (
                  <td key={et.entity_type} className="py-1 pr-2">
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 text-left"
                        onClick={() =>
                          setOpen({
                            rule: {
                              roleName: role.name,
                              entityType: et.entity_type,
                            },
                            fields: null,
                            owner: null,
                          })
                        }
                        data-testid={`open-rule-${role.name}-${et.entity_type}`}
                      >
                        {t("admin.record_permissions.cell.ownership")}
                      </button>
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 text-left"
                        onClick={() =>
                          setOpen({
                            rule: null,
                            fields: {
                              roleName: role.name,
                              entityType: et.entity_type,
                              fields: et.fields ?? [],
                            },
                            owner: null,
                          })
                        }
                        data-testid={`open-fields-${role.name}-${et.entity_type}`}
                      >
                        {t("admin.record_permissions.cell.fields")}
                      </button>
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 text-left"
                        onClick={() =>
                          setOpen({
                            rule: null,
                            fields: null,
                            owner: {
                              entityType: et.entity_type,
                              fields: et.fields ?? [],
                            },
                          })
                        }
                        data-testid={`open-owner-${et.entity_type}`}
                      >
                        {t("admin.record_permissions.cell.owner")}
                      </button>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open.rule ? (
        <RecordRuleEditorDialog
          open
          roleName={open.rule.roleName}
          entityType={open.rule.entityType}
          onClose={() => setOpen(INITIAL)}
          onSaved={() => setOpen(INITIAL)}
          onConflict={(serverVersion) =>
            onConflict(
              open.rule!.roleName,
              open.rule!.entityType,
              serverVersion,
            )
          }
        />
      ) : null}

      {open.fields ? (
        <FieldPermissionsDialog
          open
          roleName={open.fields.roleName}
          entityType={open.fields.entityType}
          fields={open.fields.fields}
          onClose={() => setOpen(INITIAL)}
          onSaved={() => setOpen(INITIAL)}
          onConflict={(serverVersion, serverGrants) =>
            onConflict(
              open.fields!.roleName,
              open.fields!.entityType,
              serverVersion,
              serverGrants,
            )
          }
        />
      ) : null}

      {open.owner ? (
        <EntityFieldOwnerDialog
          open
          entityType={open.owner.entityType}
          fields={open.owner.fields}
          onClose={() => setOpen(INITIAL)}
          onSaved={() => setOpen(INITIAL)}
          onConflict={(serverVersion, serverOwnerField) =>
            onConflict(
              "",
              open.owner!.entityType,
              serverVersion,
              undefined,
              serverOwnerField,
            )
          }
        />
      ) : null}

      <RecordPermissionConflictDialog
        open={conflict !== null}
        conflict={conflict}
        onRefetch={() => setConflict(null)}
        onMerge={() => setConflict(null)}
        onDiscard={() => setConflict(null)}
        onClose={() => setConflict(null)}
      />
    </div>
  );
}
